import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorDetails } from "../../../app/appUtils";
import { Spinner } from "../../../components/ui/common";
import { Modal } from "../../../components/ui/Modal";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import { Vehicle } from "../@types";
import { useDeleteVehicleMutation } from "../vehicleApiSlice";

interface DeleteVehicleProps {
  vehicle: Vehicle;
  isOpen: boolean;
  onClose: () => void;
}

const DeleteVehicleModal: React.FC<DeleteVehicleProps> = ({
  vehicle,
  onClose,
  isOpen,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const [deleteVehicle, { isLoading, isSuccess, isError, error }] =
    useDeleteVehicleMutation();

  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      await deleteVehicle(vehicle.id).unwrap();
      setShowSuccess(true);
    } catch (err) {
      console.error("Delete error:", err);
      const errorMessage =
        getErrorDetails(err).message ||
        "Failed to delete vehicle. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: "Vehicle deleted successfully",
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        "Failed to delete vehicle. Please try again.";
      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    onClose();
    navigate("/admin/vehicles");
  };

  const renderWarningView = () => (
    <div className="p-6 text-center space-y-14">
      {/* Warning Icon */}
      <div className="mx-auto mb-6 w-full h-full rounded-full flex items-center justify-center">
        <svg
          className="text-[#E8BB00] w-[100px] h-[100px]"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="10"
            strokeWidth="1.5"
            d="M12 16h.008M12 8v5m10-1c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10s10-4.477 10-10"
          />
        </svg>
      </div>

      {/* Warning Text */}

      <p className="text-hColor text-[18px] mb-2">
        Do you want to take this action?
      </p>

      {/* Action Buttons */}
      <div className="flex justify-center items-center space-x-6">
        <button
          type="button"
          onClick={handleCloseModal}
          disabled={isLoading}
          className="px-6 py-2 bg-[#EAEAEA] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
        >
          No, go back
        </button>

        <button
          type="button"
          onClick={handleDelete}
          disabled={isLoading}
          className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 text-[15px]"
        >
          {isLoading ? <Spinner /> : "Yes, Delete"}
        </button>
      </div>
    </div>
  );

  const renderSuccessView = () => (
    <SuccessView
      handleCloseModal={handleCloseModal}
      heading=""
      message="Vehicle has been deleted successfully."
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={showSuccess ? "" : ""}
      className="max-w-[480px] w-full bg-white px-2 pb-6 overflow-hidden"
    >
      {showSuccess ? renderSuccessView() : renderWarningView()}
    </Modal>
  );
};

export default DeleteVehicleModal;
